import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { isTranslatorCrash } from '@/lib/isTranslatorCrash';
import { reloadPage } from '@/lib/reloadPage';
import { reportCrash } from '@/lib/crashReporter';

/**
 * Экран вместо упавшей страницы — `errorElement` у `/app`, `/curator`, `/admin`
 * (техдолг 263).
 *
 * ⚠️ ЗАЧЕМ. Без него React Router показывает свою заглушку для разработчика:
 * «Unexpected Application Error!», английский текст и стек. Ученик с переводчиком
 * браузера видел ровно это (жалоба 15.09.2026) и не понимал, что делать.
 *
 * ⚠️ СТЕК НЕ ПОКАЗЫВАЕМ. Ученику он ничего не скажет, а разработчику ошибка
 * доступна в консоли браузера.
 *
 * ⚠️ «Обновить» — `reloadPage()` (`location.reload()`), а не переход на главную: ученик остаётся
 * на том же адресе (в том же уроке) и продолжает с того места, где был.
 */
export function RouteErrorScreen() {
  const { t } = useTranslation();
  const error = useRouteError();
  console.error(error);
  // Отчёт о падении (T23): в приложении уходит на сервер, на сайте — ничего.
  useEffect(() => reportCrash('js-error', error), [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="max-w-md text-center">
        <span className="material-symbols-outlined text-5xl text-primary" aria-hidden>
          refresh
        </span>
        <CardTitle>{t('errorScreen.title')}</CardTitle>
        <p className="mt-2 text-sm text-on-surface-variant">{t('errorScreen.body')}</p>
        {isTranslatorCrash(error) && (
          <p data-translator-hint className="mt-2 text-sm text-on-surface-variant">
            {t('errorScreen.translatorHint')}
          </p>
        )}
        <Button className="mt-5" fullWidth onClick={reloadPage}>
          {t('errorScreen.reload')}
        </Button>
      </Card>
    </div>
  );
}
